import { DAYS } from "@modules/irve/constants"
import { formatDate } from "@core/helpers"
import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import {
    IrveAnalysis,
    IrveFoisonnement,
    IrveFormBorne,
    IrveFormProfileRow,
} from "@types"
// =====================
// Type definitions des réponses des services
// =====================
export type AnalyseByBuildingResponse = {
    data: IrveAnalysis[]
}
export type DeleteAnalysisByIdErrorResponse = {
    detail: {
        code: string
        message: string
        description?: string
    }
}

export type EditAnalysisNameResponse = IrveAnalysis
export type GetAnalysisByIdResponse = IrveAnalysis

/**
 * Service pour gérer les analyses d'un bâtiment.
 */
export class IrveService {
    /**
     * Récupère une analyse à l'aide de son id
     * @param _id_analysis - Identifiant de l'analyse
     * @param controller - Contrôleur d'annulation de la requête
     */
    async getAnalysisById(
        _id_analysis: string,
        controller: AbortController = new AbortController()
    ): Promise<GetAnalysisByIdResponse> {
        return handleServiceCall<GetAnalysisByIdResponse>(async () => {
            const { data } = await axios.get(
                `/api/irve/residual_power/analysis/${_id_analysis}`,
                { signal: controller?.signal }
            )
            return data
        })
    }
    /**
     * Récupère les analyses d'un bâtiment
     * @param _id_building - Identifiant du bâtiment
     * @param page - Numéro de la page (optionnel)
     * @param limit - Limite de résultats par page (optionnel)
     */
    async getAnalysesByBuilding(
        _id_building: string
        // limit?: number,
        // page?: number
    ): Promise<AnalyseByBuildingResponse> {
        return handleServiceCall<AnalyseByBuildingResponse>(async () => {
            const { data } = await axios.get(
                `/api/metier/analyse/building/${_id_building}`
            )
            return data
        })
    }

    /**
     * Supprime une analyse à l'aide de son id
     * @param _id_analysis - Identifiant de l'analyse
     */
    async deleteAnalysisById(
        _id_analysis: string
    ): Promise<DeleteAnalysisByIdErrorResponse> {
        return handleServiceCall<DeleteAnalysisByIdErrorResponse>(async () =>
            axios.delete(`/api/metier/analyse/${_id_analysis}`)
        )
    }

    /**
     * Modifie le nom d'une analyse
     * @param _id_analysis - Identifiant de l'analyse
     * @param name - Nouveau nom de l'analyse
     * @returns Analyse modifiée
     */
    async editAnalysisName(
        _id_analysis: string,
        name: string
    ): Promise<EditAnalysisNameResponse> {
        return handleServiceCall<EditAnalysisNameResponse>(async () =>
            axios.put(`/api/metier/analyse/${_id_analysis}`, {
                data: { name },
            })
        )
    }
    /**
     * Création d'une analyse de puissance résiduelle
     * @param _id_source - Identifiant de la source
     * @param _id_company - Identifiant de l'entreprise
     * @param _id_user - Identifiant de l'utilisateur
     * @param _id_building - Identifiant du bâtiment
     * @param name - Nom de l'analyse
     * @param bornes - Bornes de l'analyse
     * @param utilisationProfile - Profils d'utilisation du bâtiment + foisonnement
     * @param dateTo - Date de fin de l'analyse
     * @param installation_type - Type d'installation
     */
    async createAnalysisResidualPower({
        _id_source,
        _id_company,
        _id_user,
        _id_building,
        bornes,
        utilisationProfile,
        dateTo,
        dateFrom,
        installation_type,
        usage,
        name = "",
    }: {
        _id_source: string
        _id_company: string
        _id_user: string
        _id_building: string
        bornes: IrveFormBorne[]
        utilisationProfile: IrveFormProfileRow[]
        dateTo: Date
        dateFrom: Date
        installation_type: string
        usage: string
        name?: string
    }): Promise<GetAnalysisByIdResponse> {
        // Mapping à plat des bornes en fonction de la quantité de chaque borne pour l'envoi à l'API
        const bornesBody = bornes.flatMap((borne) => {
            return Array.from({ length: borne.quantity }).map(() => {
                return {
                    cos_phi: borne.powerFactor,
                    puissanceactive_moyenne: borne.powerMean,
                    puissanceactive_nominale: borne.power,
                }
            })
        })

        const date_to = formatDate(dateTo, "YYYY-MM-DD")
        const date_from = formatDate(dateFrom, "YYYY-MM-DD")

        // Mapping des jours en français à leurs index correspondant
        const dayMapping: Record<string, number> = DAYS.reduce(
            (acc, day: string, index) => {
                acc[day.toLowerCase()] = index
                return acc
            },
            {} as Record<string, number>
        )

        // Initialiser un tableau pour toutes les heures de la semaine
        const foisonnement: IrveFoisonnement[] = Array.from({
            length: 7 * 24,
        }).map((_, index) => {
            return {
                day_fr: Math.floor(index / 24),
                hour_fr: index % 24,
                coefficient_foisonnement: 0,
            }
        })

        // Remplir le tableau avec les valeurs du profile d'utilisation
        utilisationProfile.forEach(({ days, periods, foisonnement: value }) => {
            days.forEach((day) => {
                // récupère l'index du jour
                const dayIndex = dayMapping[day]

                // Pour chaque période, ajouter une entrée dans le tableau
                periods.forEach(({ start, end }) => {
                    let _end = end
                    let _start = start
                    // dans le cas où la période se termine à minuit, on prend 24h comme fin
                    if (end === 0 && start !== 0) {
                        _end = 24
                    }
                    // dans le cas où la période est toute la journée, on prend 24h comme fin et 0h comme début
                    else if (end === 0 && start === 0) {
                        _end = 24
                        _start = 0
                    }

                    for (let hour = _start; hour < _end; hour++) {
                        const index = dayIndex * 24 + hour
                        if (foisonnement[index]) {
                            foisonnement[index].coefficient_foisonnement =
                                value ?? 0
                        }
                    }
                })
            })
        })

        return handleServiceCall<GetAnalysisByIdResponse>(async () => {
            const { data } = await axios.post(
                "/api/irve/residual_power/analysis",
                {
                    _id_source,
                    _id_company,
                    _id_building,
                    _id_user,
                    name,
                    bornes: bornesBody,
                    foisonnement,
                    date_to,
                    date_from,
                    installation_type,
                    usage,
                }
            )

            return data
        })
    }
}
